import React, { useState, useEffect, useRef, useMemo } from 'react'
import { isNil } from 'ramda'
import { ChevronDown } from 'lucide-react'

import AddToGroup from './actions/AddToGroup'
import ExportCSV from './actions/ExportCSV'
import Invite from './actions/Invite'
import Approve from './actions/Approve'
import Delete from 'app/componentV4/actions/Delete'
import { Typography } from '@material-ui/core'

const CONTAINER_WIDTH = {
  INITIAL: 836,
  BREAKPOINT: 835,
  BUTTON_REDUCTION_STEP: 110,
}

const BulkActionsButtonGroup = ({
  selectedIdsArray,
  isSelectAllMode,
  getSelectedCount,
  openBulkAssignGroupDialog,
  mes_3841_show_crm_export,
  mes_5173_crm_bulk_delete_subscribers,
  selectedInvitedCount,
  handleIndividualDialogAction,
  invitedSelected,
  totalSelectedCount,
  selectedPendingApproveCount,
  approveSelected,
  formatMessage,
  selectedIds,
  meta,
}) => {
  const [containerWidth, setContainerWidth] = useState(CONTAINER_WIDTH.INITIAL)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const containerRef = useRef(null)
  const dropdownRef = useRef(null)

  const buttons = useMemo(() => {
    const buttonList = []

    if (selectedIdsArray?.length > 0) {
      buttonList.push({
        id: 'add-to-group',
        component: AddToGroup,
        props: {
          selectedCount: getSelectedCount(),
          onToggle: openBulkAssignGroupDialog,
        },
      })
    }

    if (mes_3841_show_crm_export) {
      buttonList.push({
        id: 'export-csv',
        component: ExportCSV,
        props: {
          selectedIds: selectedIds[meta.currentPage],
          totalSelectedCount: totalSelectedCount,
        },
      })
    }

    if (!isSelectAllMode && selectedInvitedCount > 0) {
      buttonList.push({
        id: 'invite',
        component: Invite,
        props: {
          onAction: handleIndividualDialogAction,
          selected: invitedSelected,
          selectedCount: selectedInvitedCount,
          totalSelected: totalSelectedCount,
        },
      })
    }

    if (!isSelectAllMode && selectedPendingApproveCount > 0) {
      buttonList.push({
        id: 'approve',
        component: Approve,
        props: {
          onAction: handleIndividualDialogAction,
          selected: approveSelected,
          selectedCount: selectedPendingApproveCount,
          totalSelected: totalSelectedCount,
        },
      })
    }

    if (selectedIdsArray?.length > 0) {
      const shouldShowDelete =
        !isSelectAllMode || mes_5173_crm_bulk_delete_subscribers

      if (shouldShowDelete) {
        const deleteCount = isSelectAllMode
          ? getSelectedCount()
          : selectedIdsArray.length

        buttonList.push({
          id: 'delete',
          component: Delete,
          props: {
            title: formatMessage(
              {
                id: 'subscriber.delete.title',
              },
              {
                count: deleteCount,
              },
            ),
            description: formatMessage(
              {
                id: 'subscriber.delete.description',
              },
              {
                count: deleteCount,
              },
            ),
            onAction: handleIndividualDialogAction,
            selected: selectedIdsArray,
            selectedCount: deleteCount,
          },
        })
      }
    }

    return buttonList
  }, [
    selectedIdsArray,
    isSelectAllMode,
    getSelectedCount,
    openBulkAssignGroupDialog,
    mes_3841_show_crm_export,
    mes_5173_crm_bulk_delete_subscribers,
    selectedIds,
    meta,
    totalSelectedCount,
    selectedInvitedCount,
    handleIndividualDialogAction,
    invitedSelected,
    selectedPendingApproveCount,
    approveSelected,
    formatMessage,
  ])

  const getVisibleCount = (width) => {
    if (buttons.length === 1) return 1

    if (width >= CONTAINER_WIDTH.BREAKPOINT) return buttons.length

    const reduction = CONTAINER_WIDTH.BREAKPOINT - width
    const buttonsToHide =
      Math.floor(reduction / CONTAINER_WIDTH.BUTTON_REDUCTION_STEP) + 1

    const calculatedVisible = Math.max(0, buttons.length - buttonsToHide)
    const wouldHideCount = buttons.length - calculatedVisible

    if (wouldHideCount === 1) {
      return buttons.length
    }

    return calculatedVisible
  }

  const visibleCount = getVisibleCount(containerWidth)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width
      setContainerWidth(width)
    })

    resizeObserver.observe(container)
    setContainerWidth(container.offsetWidth)

    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideDialog =
        event.target.closest('[role="dialog"]') ||
        event.target.closest('.MuiDialog-root')

      if (isInsideDialog) {
        return
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  const hiddenButtons = buttons.slice(visibleCount)

  return (
    <div
      ref={containerRef}
      className="flex items-center grow justify-end h-full gap-5"
    >
      {visibleCount > 0 && (
        <div className="flex items-center gap-5">
          {buttons.map((button, index) => {
            const Component = button.component
            const isVisible = index < visibleCount
            const isLastVisible = index === visibleCount - 1

            return (
              <div
                key={button.id}
                style={{ display: isVisible ? 'block' : 'none' }}
                className={
                  !isLastVisible
                    ? 'border-r border-neutrals-150 h-full pr-5'
                    : ''
                }
              >
                <Component {...button.props} />
              </div>
            )
          })}
        </div>
      )}

      {hiddenButtons.length >= 2 && (
        <div
          className={`relative ${visibleCount > 0 ? 'border-l border-neutrals-150 h-full pl-5' : ''}`}
          ref={dropdownRef}
        >
          <div className={visibleCount > 0 ? '' : 'pl-5'}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-1 text-14 text-primary font-500 cursor-pointer whitespace-nowrap"
            >
              <Typography
                component="span"
                className="text-14 text-primary font-500 cursor-pointer whitespace-nowrap"
              >
                +{hiddenButtons.length}{' '}
                {formatMessage({ id: 'bulk.action.dropdown.label' })}
              </Typography>
              <ChevronDown
                size={16}
                className={`transition-transform ${
                  isDropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>

          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 bg-white rounded-[6px] shadow-lg z-99 w-fit whitespace-nowrap overflow-hidden p-2">
              {hiddenButtons.map((button) => {
                const Component = button.component
                return (
                  <div
                    key={button.id}
                    className={`px-3 py-2 hover:bg-neutrals-100 cursor-pointer`}
                  >
                    <Component
                      {...button.props}
                      isInDropdown={button.id === 'delete'}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default BulkActionsButtonGroup
